import { MessageService } from 'primeng/api';
import { AuthService } from 'src/app/core/services/auth.service';
import { storageConstant } from 'src/app/constants/storage-constant';

const POST_LOGOUT_TOAST_DETAIL =
  'For your security, you have been logged out of your account. Please log back in to continue using CollegeRecon.';

/** Survives `localStorage.clear()`; consumed on the login page where `<toaster>` is mounted. */
export function setPostLogoutSuccessToast(
  detail: string = POST_LOGOUT_TOAST_DETAIL,
): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(
    storageConstant.POST_LOGOUT_TOAST,
    JSON.stringify({
      severity: 'success',
      summary: 'Success',
      detail,
    }),
  );
}

export function showPostLogoutToastIfPresent(messageService: MessageService): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  const raw = sessionStorage.getItem(storageConstant.POST_LOGOUT_TOAST);
  if (!raw) {
    return;
  }
  sessionStorage.removeItem(storageConstant.POST_LOGOUT_TOAST);
  try {
    const message = JSON.parse(raw);
    // Defer until login `<toaster>` / p-toast is in the DOM.
    queueMicrotask(() => messageService.add(message));
  } catch {
    // ignore malformed payload
  }
}

/**
 * True when persisted session data exists for the logged-in user but there is no
 * usable JWT. The API expects Bearer auth — these sessions must re-authenticate.
 */
export function isStoredSessionMissingToken(): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  try {
    const userRaw = localStorage.getItem(storageConstant.LOGGED_IN_USER);
    if (userRaw == null || userRaw.trim() === '') {
      return false;
    }
    const userParsed = JSON.parse(userRaw);
    if (userParsed == null || userParsed === '') {
      return false;
    }

    const tokenRaw = localStorage.getItem(storageConstant.LOGGED_IN_TOKEN);
    if (tokenRaw == null || tokenRaw === '') {
      return true;
    }
    const token = JSON.parse(tokenRaw);
    if (token == null) {
      return true;
    }
    if (typeof token !== 'string') {
      return true;
    }
    return token.trim().length === 0;
  } catch {
    return false;
  }
}

/** Clears storage and sends the user to login (same outcome as AuthService.logout). */
export function forceLogoutStaleSession(auth: AuthService): void {
  auth.logout(true);
}
